/* ==============================================================================
   Diálogos da aplicação
   ==============================================================================
   Substitui `window.prompt` e `window.confirm`.

   Por que os nativos tinham que sair
   ==================================
   Não é só estética. O diálogo nativo do navegador:

     · BLOQUEIA a thread principal. Durante uma chamada, isso congela o
       processamento de áudio e a renegociação WebRTC — um `confirm()` aberto por
       10 segundos é uma chamada travada por 10 segundos.
     · Mostra o domínio ("concord.fuxar.online diz"), que parece aviso de
       segurança do navegador e não uma pergunta do produto.
     · Não aceita tema, foco, teclado customizado, validação nem contexto.
     · É bloqueável pelo navegador. Um segundo `prompt()` seguido pode
       simplesmente não aparecer, e o código recebe `null` como se o usuário
       tivesse cancelado.

   O último item é o pior: um cancelamento que a pessoa não fez, indistinguível
   de um que ela fez.

   Como este substituto funciona
   =============================
   `useDialog()` devolve funções que retornam PROMISE. O chamador escreve o mesmo
   código de antes — `const nome = await ask(...)` — e o resto muda: renderiza
   dentro da árvore, respeita tema, aceita validação, e não bloqueia nada.
   ============================================================================== */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/* ------------------------------------------------------------------------------
   Contrato
------------------------------------------------------------------------------ */

export interface AskOptions {
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  maxLength?: number;
  /** Devolve a mensagem de erro, ou `null` quando o valor serve. */
  validate?: (value: string) => string | null;
}

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Marca a ação como destrutiva: botão vermelho, e o foco vai no cancelar. */
  danger?: boolean;
  /**
   * Exige digitar este texto para liberar a confirmação.
   *
   * Para ação irreversível de verdade — apagar servidor, remover conta. Um
   * `confirm()` simples é clicado no automático; digitar o nome obriga a ler.
   */
  requireTyping?: string;
}

interface DialogApi {
  /** Pergunta um texto. Resolve com `null` se cancelado. */
  ask: (options: AskOptions) => Promise<string | null>;
  /** Pede confirmação. Resolve `true` ou `false`. */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const DialogContext = createContext<DialogApi | null>(null);

/**
 * Acessa os diálogos.
 *
 * Lança fora do provider em vez de devolver um objeto vazio: um `ask` que não
 * faz nada e resolve `null` pareceria "o usuário cancelou", e o bug ficaria
 * invisível.
 */
export function useDialog(): DialogApi {
  const api = useContext(DialogContext);
  if (api === null) {
    throw new Error(
      'useDialog() fora de <DialogProvider>. Envolva a árvore do app com ele.',
    );
  }
  return api;
}

/* ------------------------------------------------------------------------------
   Estado interno
------------------------------------------------------------------------------ */

type Pending =
  | {
      kind: 'ask';
      options: AskOptions;
      resolve: (value: string | null) => void;
    }
  | {
      kind: 'confirm';
      options: ConfirmOptions;
      resolve: (value: boolean) => void;
    };

export function DialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const api = useMemo<DialogApi>(
    () => ({
      ask: (options) =>
        new Promise<string | null>((resolve) => {
          setPending({ kind: 'ask', options, resolve });
        }),
      confirm: (options) =>
        new Promise<boolean>((resolve) => {
          setPending({ kind: 'confirm', options, resolve });
        }),
    }),
    [],
  );

  /*
   * Um diálogo por vez, e o anterior é CANCELADO se outro abrir.
   *
   * Sem isto, a promise do primeiro ficaria pendente para sempre e o `await` do
   * chamador nunca retornaria — vazamento silencioso que aparece como um botão
   * que "não faz nada" depois de um tempo de uso.
   */
  const close = useCallback(
    (result: string | null | boolean) => {
      setPending((current) => {
        if (current === null) return null;
        if (current.kind === 'ask') {
          current.resolve(typeof result === 'string' ? result : null);
        } else {
          current.resolve(result === true);
        }
        return null;
      });
    },
    [],
  );

  return (
    <DialogContext.Provider value={api}>
      {children}
      {pending !== null && <DialogSurface pending={pending} onClose={close} />}
    </DialogContext.Provider>
  );
}

/* ------------------------------------------------------------------------------
   Superfície
------------------------------------------------------------------------------ */

function DialogSurface({
  pending,
  onClose,
}: {
  pending: Pending;
  onClose: (result: string | null | boolean) => void;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const isAsk = pending.kind === 'ask';
  const danger = !isAsk && pending.options.danger === true;
  const requireTyping = !isAsk ? pending.options.requireTyping : undefined;

  const [value, setValue] = useState(
    isAsk ? (pending.options.initialValue ?? '') : '',
  );
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  /*
   * O foco vai para o campo em `ask`, e para CANCELAR em ação destrutiva.
   *
   * Foco no botão destrutivo faria Enter apagar o servidor — e Enter é o que a
   * pessoa aperta por reflexo ao ver um diálogo. O padrão seguro é o inverso.
   */
  useEffect(() => {
    const target = isAsk ? inputRef.current : cancelRef.current;
    target?.focus();
    if (isAsk) inputRef.current?.select();
  }, [isAsk]);

  /* ---- Escape fecha, Tab fica preso dentro ---- */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose(isAsk ? null : false);
        return;
      }

      /*
       * Armadilha de foco.
       *
       * Sem ela, Tab sai do diálogo e passeia pela interface atrás dele — que
       * está inerte e não deveria receber foco. É o requisito de um diálogo
       * modal, e é o que `window.confirm` dava de graça.
       */
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;

      const focusable = panel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isAsk, onClose]);

  const submit = useCallback(() => {
    if (isAsk) {
      const trimmed = value.trim();
      const message = pending.options.validate?.(trimmed) ?? null;
      if (message !== null) {
        setError(message);
        inputRef.current?.focus();
        return;
      }
      onClose(trimmed);
      return;
    }
    onClose(true);
  }, [isAsk, value, pending, onClose]);

  const confirmBlocked =
    requireTyping !== undefined && typed.trim() !== requireTyping;

  return (
    <div
      className="fixed inset-0 grid place-items-center px-4"
      style={{ zIndex: 'var(--z-modal)' }}
    >
      {/* ----------------------------------------------------------------------
          Cortina
          ----------------------------------------------------------------------
          Escurece E desfoca o que está atrás. Só escurecer deixa a interface
          legível por baixo, e a atenção se divide; o blur remove a competição
          sem esconder o contexto.
      ---------------------------------------------------------------------- */}
      <button
        type="button"
        aria-label="Fechar"
        onClick={() => onClose(isAsk ? null : false)}
        className="absolute inset-0 cursor-default"
        style={{
          background: 'oklch(4% 0.02 292 / 0.62)',
          backdropFilter: 'blur(10px) saturate(140%)',
          WebkitBackdropFilter: 'blur(10px) saturate(140%)',
          animation: 'concord-fade-in var(--duration-fast) ease-out both',
        }}
      />

      <div
        ref={panelRef}
        role={danger ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        {...(pending.options.description !== undefined
          ? { 'aria-describedby': descriptionId }
          : {})}
        className="glass relative w-full"
        style={{
          maxWidth: 440,
          padding: 'var(--spacing-5)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: danger
            ? 'var(--glass-edge), 0 24px 64px oklch(0% 0 0 / 0.55), 0 0 0 1px var(--color-danger-subtle)'
            : 'var(--glass-edge), 0 24px 64px oklch(0% 0 0 / 0.55), var(--glow-medium)',
          animation:
            'concord-dialog-in var(--duration-normal) var(--ease-spring) both',
        }}
      >
        <style>{`
          @keyframes concord-fade-in { from { opacity: 0 } to { opacity: 1 } }
          @keyframes concord-dialog-in {
            from { opacity: 0; transform: translateY(10px) scale(0.97) }
            to   { opacity: 1; transform: none }
          }
          @media (prefers-reduced-motion: reduce) {
            @keyframes concord-dialog-in { from { opacity: 0 } to { opacity: 1 } }
          }
        `}</style>

        <h2
          id={titleId}
          className="font-bold"
          style={{
            fontSize: 'var(--text-base)',
            letterSpacing: 'var(--tracking-tight)',
            color: 'var(--color-text-primary)',
          }}
        >
          {pending.options.title}
        </h2>

        {pending.options.description !== undefined && (
          <p
            id={descriptionId}
            className="mt-1.5"
            style={{
              fontSize: 'var(--text-xs)',
              lineHeight: 'var(--leading-relaxed)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            {pending.options.description}
          </p>
        )}

        {/* ---- Campo de texto ---- */}
        {isAsk && (
          <div className="mt-4">
            {pending.options.label !== undefined && (
              <label
                htmlFor={`${titleId}-input`}
                className="mb-1.5 block font-medium"
                style={{
                  fontSize: 'var(--text-2xs)',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-wide)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                {pending.options.label}
              </label>
            )}
            <input
              id={`${titleId}-input`}
              ref={inputRef}
              type="text"
              value={value}
              maxLength={pending.options.maxLength ?? 200}
              placeholder={pending.options.placeholder}
              onChange={(event) => {
                setValue(event.target.value);
                if (error !== null) setError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  submit();
                }
              }}
              className="w-full px-3 outline-none transition-shadow"
              style={{
                height: 40,
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-sunken)',
                boxShadow:
                  error !== null
                    ? '0 0 0 1px var(--color-danger), 0 0 0 4px var(--color-danger-subtle)'
                    : 'inset 0 1px 0 oklch(0% 0 0 / 0.30), 0 0 0 1px var(--color-border-default)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-primary)',
              }}
              onFocus={(event) => {
                if (error === null) {
                  event.currentTarget.style.boxShadow =
                    '0 0 0 1px var(--color-accent-border), 0 0 0 4px var(--color-accent-subtle)';
                }
              }}
              onBlur={(event) => {
                if (error === null) {
                  event.currentTarget.style.boxShadow =
                    'inset 0 1px 0 oklch(0% 0 0 / 0.30), 0 0 0 1px var(--color-border-default)';
                }
              }}
            />
            {/*
              O erro anima a ALTURA de 0fr para 1fr em vez de aparecer e sumir.
              Reservar espaço vazio para um erro que talvez não venha deixa um
              buraco no diálogo; aparecer sem transição empurra os botões de
              golpe.
            */}
            <div
              className="grid transition-[grid-template-rows,opacity]"
              style={{
                gridTemplateRows: error !== null ? '1fr' : '0fr',
                opacity: error !== null ? 1 : 0,
                transitionDuration: 'var(--duration-fast)',
              }}
            >
              <p
                className="overflow-hidden"
                style={{
                  minHeight: 0,
                  paddingTop: error !== null ? 6 : 0,
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-danger-on-subtle)',
                }}
                role={error !== null ? 'alert' : undefined}
              >
                {error}
              </p>
            </div>
          </div>
        )}

        {/* ---- Confirmação por digitação ---- */}
        {requireTyping !== undefined && (
          <div className="mt-4">
            <label
              htmlFor={`${titleId}-typed`}
              className="mb-1.5 block"
              style={{
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-text-tertiary)',
              }}
            >
              Digite <strong style={{ color: 'var(--color-text-primary)' }}>
                {requireTyping}
              </strong>{' '}
              para confirmar
            </label>
            <input
              id={`${titleId}-typed`}
              type="text"
              value={typed}
              autoComplete="off"
              onChange={(event) => setTyped(event.target.value)}
              className="w-full px-3 outline-none"
              style={{
                height: 36,
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-sunken)',
                boxShadow:
                  'inset 0 1px 0 oklch(0% 0 0 / 0.30), 0 0 0 1px var(--color-border-default)',
                fontSize: 'var(--text-sm)',
                fontFamily: 'var(--font-mono)',
                color: 'var(--color-text-primary)',
              }}
            />
          </div>
        )}

        {/* ---- Ações ---- */}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={() => onClose(isAsk ? null : false)}
            className="px-3.5 font-medium transition-colors"
            style={{
              height: 36,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-surface-hover)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {pending.options.cancelLabel ?? 'Cancelar'}
          </button>

          <button
            type="button"
            onClick={submit}
            disabled={confirmBlocked || (isAsk && value.trim().length === 0)}
            className="px-4 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-40"
            style={{
              height: 36,
              borderRadius: 'var(--radius-full)',
              background: danger
                ? 'var(--color-danger-solid)'
                : 'var(--color-accent-solid)',
              color: 'var(--color-on-solid)',
              fontSize: 'var(--text-xs)',
              boxShadow: danger ? 'none' : 'var(--glow-medium)',
            }}
          >
            {pending.options.confirmLabel ?? (isAsk ? 'Confirmar' : 'Sim')}
          </button>
        </div>
      </div>
    </div>
  );
}
